import { useState, useEffect, useMemo, useCallback } from 'react';
import { fetchTaskTypeDropdowns } from '../services/api';
import { TICKET_TYPES } from '../components/common/TicketTypeSelector';

/**
 * Hook to load and manage task types from the API
 * 
 * @param {string|number|null} projectId - Optional project ID for scoped type loading
 * @returns {object} - { taskTypes, taskTypesById, loading, error, getTypeByKey, getTypeById, refreshTypes }
 */
export const useTaskTypes = (projectId = null) => {
  const [rawTypes, setRawTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Load task types from API
  const loadTypes = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const types = await fetchTaskTypeDropdowns(projectId);
      setRawTypes(types);
    } catch (err) {
      console.error('Error loading task types:', err);
      setError(err);
      setRawTypes([]);
    } finally {
      setLoading(false);
    }
  }, [projectId]);

  useEffect(() => {
    loadTypes();
  }, [loadTypes]);

  // Transform API types into usable format (keyed by key)
  const taskTypes = useMemo(() => {
    if (rawTypes.length > 0) {
      const types = {};
      rawTypes.forEach((type) => {
        types[type.key] = {
          id: type.id,
          key: type.key,
          name: type.name,
          icon: type.icon_url || TICKET_TYPES[type.key]?.icon || '📋',
          color: TICKET_TYPES[type.key]?.color || '#4c9aff',
          bgColor: TICKET_TYPES[type.key]?.bgColor || '#deebff',
          label: type.name,
          description: type.is_system_default ? 'System type' : 'Custom type',
          isCustom: !type.is_system_default,
          isSubtaskType: type.is_subtask_type,
          isParentCapable: type.is_parent_capable,
          isSystemDefault: type.is_system_default
        };
      });
      return types;
    }
    // Fallback to static types
    return { ...TICKET_TYPES };
  }, [rawTypes]);

  // Also provide types keyed by ID for lookups
  const taskTypesById = useMemo(() => {
    const byId = {};
    Object.entries(taskTypes).forEach(([key, config]) => {
      if (config.id) {
        byId[config.id] = config;
      }
    });
    return byId;
  }, [taskTypes]);

  // Get type config by key
  const getTypeByKey = useCallback((key) => {
    return taskTypes[key] || TICKET_TYPES[key] || null;
  }, [taskTypes]);

  // Get type config by ID
  const getTypeById = useCallback((id) => {
    return taskTypesById[id] || null;
  }, [taskTypesById]);

  // Get all type keys
  const typeKeys = useMemo(() => Object.keys(taskTypes), [taskTypes]);

  // Get non-subtask types (for main type selectors)
  const mainTypes = useMemo(() => {
    return Object.entries(taskTypes)
      .filter(([, config]) => !config.isSubtaskType)
      .reduce((acc, [key, config]) => {
        acc[key] = config;
        return acc;
      }, {});
  }, [taskTypes]);

  // Get subtask types only
  const subtaskTypes = useMemo(() => {
    return Object.entries(taskTypes)
      .filter(([, config]) => config.isSubtaskType)
      .reduce((acc, [key, config]) => {
        acc[key] = config;
        return acc;
      }, {});
  }, [taskTypes]);

  return {
    taskTypes,
    taskTypesById,
    typeKeys,
    mainTypes,
    subtaskTypes,
    loading,
    error,
    getTypeByKey,
    getTypeById,
    refreshTypes: loadTypes,
    rawTypes // Expose raw API response if needed
  };
};

/**
 * Utility function to get type config - can be used outside of React components
 * Falls back to TICKET_TYPES static config
 */
export const getStaticTypeConfig = (key) => {
  return TICKET_TYPES[key] || {
    icon: '📋',
    color: '#4c9aff',
    bgColor: '#deebff',
    label: key || 'Unknown',
    description: 'Task type'
  };
};

export default useTaskTypes;
